import { Link } from "react-router-dom";
import { ArrowRight, Box, Shirt, Store, Sparkles, Layers, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1532332248682-206cc786359f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const ED1 = "https://images.unsplash.com/photo-1576193929684-06c6c6a8b582?crop=entropy&cs=srgb&fm=jpg&w=900&q=80";
const ED2 = "https://images.pexels.com/photos/34878873/pexels-photo-34878873.jpeg?auto=compress&cs=tinysrgb&w=900";
const BRANDS = ["Nike", "adidas", "Levi's", "Zara", "Mango", "COS", "ASOS", "Arket", "H&M", "Carhartt WIP", "New Balance", "& Other Stories"];

const FEATURES = [
  { icon: Box, t: "3D Avatar Studio", d: "Lade ein Foto hoch, stell deine Maße ein und sieh Outfits an deiner drehbaren 3D-Figur – oder als realistisches KI-Bild.", to: "/studio", span: "md:col-span-2" },
  { icon: Layers, t: "Outfit Builder", d: "Kombiniere Teile mehrerer Marken mit deinen eigenen Klamotten.", to: "/builder" },
  { icon: Shirt, t: "Dein Kleiderschrank", d: "Fotografiere, was du zuhause hast – vyne bezieht es in jeden Look ein.", to: "/kleiderschrank" },
  { icon: Store, t: "Marktplatz", d: "Verkaufe Teile, die du nicht mehr trägst. Sicher bezahlt per PayPal.", to: "/marktplatz" },
  { icon: Sparkles, t: "KI-Stylist", d: "Frag einfach: Was ziehe ich zur Hochzeit an? Dein Stylist kennt deinen Schrank.", to: "/stylist", span: "md:col-span-2" },
];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden grain">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 md:grid-cols-12 lg:px-8 lg:py-24">
          <div className="fade-up md:col-span-6">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="vyne" className="h-16 w-16 rounded-[22%] ring-1 ring-white/10" data-testid="hero-logo" />
              <p className="label-over">KI-Fashion Studio · Multibrand · Resale</p>
            </div>
            <h1 className="mt-8 font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
              Dein Stil.<br /><em className="text-[#D4AF37]">Neu kombiniert.</em>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-neutral-400 sm:text-lg">
              vyne stellt dir Outfits aus vielen Marken und deinem eigenen Kleiderschrank zusammen – anprobiert an deinem persönlichen 3D-Avatar.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={user ? "/studio" : "/login?mode=register"} className="btn-cream" data-testid="hero-cta-primary">Avatar erstellen <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/katalog" className="btn-ghost" data-testid="hero-cta-secondary">Marken entdecken</Link>
            </div>
          </div>
          <div className="relative md:col-span-6">
            <div className="fade-up relative ml-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-[28px] border border-white/10" style={{ animationDelay: "0.15s" }}>
              <img src={HERO} alt="Fashion Editorial" className="h-full w-full object-cover" />
              <div className="absolute inset-x-4 bottom-4 rounded-2xl border border-white/10 bg-black/60 p-4 backdrop-blur-xl">
                <p className="label-over">Look des Tages</p>
                <p className="mt-1 font-serif text-xl">City Minimal · 3 Marken + 1 eigenes Teil</p>
              </div>
            </div>
            <div className="fade-up absolute -left-2 top-10 hidden w-40 overflow-hidden rounded-2xl border border-white/10 shadow-2xl sm:block lg:-left-10" style={{ animationDelay: "0.3s" }}>
              <img src={ED1} alt="Editorial" className="aspect-[3/4] w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-white/[0.06] bg-[#121215] py-6 overflow-hidden" data-testid="brand-marquee">
        <div className="marquee flex w-max gap-14 whitespace-nowrap">
          {[...BRANDS, ...BRANDS].map((b, i) => (
            <span key={i} className="font-serif text-2xl text-neutral-500">{b}</span>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <p className="label-over">Alles an einem Ort</p>
        <h2 className="mt-3 max-w-2xl font-serif text-3xl tracking-tight sm:text-4xl">Vom Kleiderschrank zum perfekten Look – in Minuten.</h2>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {FEATURES.map(({ icon: Icon, t, d, to, span }, i) => (
            <Link key={t} to={to} className={`card-lux group fade-up flex flex-col justify-between p-8 hover:-translate-y-1 ${span || ""}`} style={{ animationDelay: `${i * 0.08}s` }} data-testid={`feature-card-${to.slice(1)}`}>
              <Icon className="h-7 w-7 text-[#D4AF37]" />
              <div className="mt-12">
                <h3 className="font-serif text-2xl">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{d}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-300 group-hover:text-[#D4AF37]">Öffnen <ArrowUpRight className="h-3.5 w-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 overflow-hidden rounded-[28px] border border-white/10 bg-[#FAF7F0] text-[#0A0A0B] md:grid-cols-2">
          <div className="flex flex-col justify-center p-10 lg:p-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A3824E]">Verkaufen wie bei Vinted</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight">Dein Schrank ist voll? Mach ihn zu Geld.</h2>
            <p className="mt-4 text-neutral-600">Biete Teile aus deinem digitalen Kleiderschrank mit einem Klick an. Käufer zahlen sicher über PayPal, du bekommst den Erlös abzüglich einer kleinen vyne-Provision.</p>
            <Link to="/marktplatz/verkaufen" className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#0A0A0B] px-6 py-3 text-sm font-semibold text-[#FAF7F0] transition-transform hover:scale-[1.03]" data-testid="landing-sell-cta">Jetzt verkaufen <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <img src={ED2} alt="Marktplatz" className="h-full max-h-[520px] w-full object-cover" />
        </div>
      </section>
    </div>
  );
}
